import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { ProcessSteps } from '../components/ProcessSteps';
import { Button } from '../components/Button';
export function HowItWorks() {
  const navigate = useNavigate();
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-1043">
      <Helmet data-id="element-1044">
        <title data-id="element-1045">How It Works | Affordable Medical Tourism</title>
        <meta name="description" content="Learn about our streamlined process for coordinating your medical care abroad. From initial consultation to post-operative recovery, we manage the logistics." data-id="element-1046" />
        <link rel="canonical" href="https://affordablemedicaltourism.com/how-it-works" data-id="element-1047" />
      </Helmet>

      <AppHeader data-id="element-1048" />

      <main className="flex-1" data-id="element-1049">
        <div className="bg-slate-50 py-20" data-id="element-1050">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1051">
            <div className="text-center mb-16" data-id="element-1052">
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-1053">
                How It Works
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed" data-id="element-1054">
                Navigating international healthcare can be complex. We serve as
                your dedicated care coordination partner, streamlining the
                entire process so you can focus entirely on your health and
                recovery.
              </p>
            </div>

            <ProcessSteps heading="Your structured care journey" intro="Our methodology is designed to provide transparency, security, and comprehensive support at every phase of your medical travel experience." steps={[{
            title: '1. Initial Inquiry & Needs Assessment',
            description: 'The process begins when you submit a secure inquiry through our contact form. A dedicated care coordinator will review your specific medical requirements, preferred timeline, and destination preferences. We maintain strict confidentiality protocols throughout this assessment phase.'
          }, {
            title: '2. Medical Review & Matched Proposals',
            description: 'We securely transmit your medical records and diagnostic imaging to specialists within our vetted network. The international medical teams review your case to confirm candidacy. We then present you with comprehensive, transparent proposals detailing the treatment plan, facility credentials, and inclusive pricing.'
          }, {
            title: '3. Logistics & Travel Coordination',
            description: 'Once you select a facility and treatment plan, our team assists with all logistical arrangements. We connect you with trusted local facilitators who manage on-the-ground details, including airport transfers, translation services, and securing appropriate accommodations in established medical tourism settlement cities. We ensure your itinerary is optimized for a stress-free arrival.'
          }, {
            title: '4. Treatment & Supported Recovery',
            description: 'Upon arrival, you are greeted by local facilitators who guide you through hospital admission. Following your procedure, you will recover in a comfortable, medically appropriate setting, often in a city tailored for long-term medical settlement. Our team remains in continuous contact with the hospital staff to monitor your progress until you are medically cleared to return home.'
          }]} data-id="element-1055" />
          </div>
        </div>

        <div className="py-24 px-6 md:px-12 max-w-4xl mx-auto" data-id="element-1056">
          <h2 className="text-3xl font-bold text-slate-900 mb-8" data-id="element-1057">
            The Value of Care Coordination
          </h2>

          <div className="prose prose-slate prose-lg max-w-none" data-id="element-1058">
            <p data-id="element-1059">
              The global healthcare landscape offers exceptional clinical
              quality and significant cost efficiencies, but accessing these
              benefits requires rigorous due diligence and logistical expertise.
              Operating independently in a foreign medical system presents
              inherent risks, from language barriers to verifying institutional
              accreditations.
            </p>
            <p data-id="element-1060">
              Our care coordination model mitigates these risks. We act as an
              informed intermediary, leveraging our established relationships
              with top-tier international hospitals and clinics. Our vetting
              standards are uncompromising. We evaluate facilities based on
              objective clinical metrics, international accreditation status
              (such as JCI), and the specific board certifications of the
              attending surgeons.
            </p>
            <p data-id="element-1061">
              We also recognize the growing importance of medical tourism
              settlement cities. For patients requiring long-term care,
              rehabilitation, or those seeking a health-focused retirement, we
              identify destinations that offer not just excellent hospitals, but
              also supportive infrastructure, assisted living options, and
              favorable long-stay visa pathways.
            </p>
            <p data-id="element-1062">
              Navigating the financial aspects of medical travel is another
              critical component. While many patients pay out-of-pocket to
              realize significant savings, we also work with individuals
              utilizing self-funded employer insurance plans that have begun to
              incorporate medical tourism benefits. We assist in providing the
              necessary documentation and transparent pricing required for these
              arrangements.
            </p>
            <p data-id="element-1063">
              Furthermore, we understand that medical travel is not merely a
              transaction; it is a significant life event requiring empathy and
              robust support. Your dedicated care coordinator serves as your
              single point of contact, advocating for your needs and ensuring
              clear communication between you and the international medical
              team.
            </p>
            <p data-id="element-1064">
              By managing the complex logistics of medical travel, we empower
              patients to make informed decisions about their healthcare. Our
              goal is to provide a seamless, secure pathway to world-class
              medical treatments, ensuring that your focus remains exactly where
              it should be: on your successful treatment and recovery.
            </p>
          </div>

          <div className="mt-12 text-center" data-id="element-1065">
            <Button size="lg" onClick={() => navigate('/contact')} data-id="element-1066">
              Speak with a care coordinator
            </Button>
          </div>
        </div>
      </main>

      <AppFooter data-id="element-1067" />
    </div>;
}